import { Controller } from "@hotwired/stimulus"

// Confirmación V2: reemplaza el confirm() nativo del navegador (diálogo
// genérico del SO, fuera de la página) por un modal del design system,
// centrado como los demás popups (feedback UI: "Eliminar Traspaso" salía con
// temática de web genérica y descentrado).
//
// Turbo 8 llama a `Turbo.config.forms.confirm(message, form, submitter)`
// antes de despachar TODA submission con data-turbo-confirm — forms reales y
// links data-turbo-method (Turbo los convierte a form copiando el atributo) —
// y AWAITEA la Promise: resolver true sigue la submission, false la aborta.
// No se interceptan clicks ni se re-emiten requests: el propio Turbo decide.
//
// El diálogo se clona del <template> del layout (inerte hasta entonces) y se
// appendea a <body>. Es transitorio — se remueve al responder, ANTES de
// cualquier navegación — así que no interactúa con el morphing ni deja estado
// en el DOM. Reusa el chrome del modal (.tp-modal__*, _modal.scss) con los
// ajustes de .tp-confirm (_confirm.scss).
export default class extends Controller {
  static targets = ["template"]

  connect() {
    // Sin optional chaining en la asignación: `window.Turbo?.config.x = …`
    // es sintaxis experimental que el Babel del pack no soporta.
    if (window.Turbo) window.Turbo.config.forms.confirm = this.confirm.bind(this)
    // Título propio del triggereo ("Eliminar X · Traspaso" vs el genérico
    // "Confirmar acción"): llega por data-turbo-confirm-title del botón. Se
    // recuerda en el click (captura, antes de que cualquiera lo cancele)
    // porque Turbo sintetiza un form para los links data-turbo-method y los
    // data-* propios del link NO llegan hasta acá con la submission.
    this.stashedTitle = null
    this.rememberTitleBound = this.rememberTitle.bind(this)
    document.addEventListener("click", this.rememberTitleBound, true)
  }

  disconnect() {
    document.removeEventListener("click", this.rememberTitleBound, true)
  }

  rememberTitle(event) {
    const trigger = event.target.closest("[data-turbo-confirm-title]")
    this.stashedTitle = trigger ? trigger.getAttribute("data-turbo-confirm-title") : null
    this.stashedHierarchy = trigger ? trigger.getAttribute("data-turbo-confirm-hierarchy") : null
  }

  // El diálogo se appendea al elemento en pantalla completa si lo hay: el
  // fullscreen deja INERTE todo lo que queda fuera de su subárbol (mismo
  // problema que adoptIntoFullscreen del modal_controller — los botones con
  // confirm que viven dentro del bloque de gráficos abrían un diálogo que no
  // se veía y la promesa quedaba colgada: el click parecía no hacer nada).
  confirm(message) {
    return new Promise((resolve) => {
      this.resolve = resolve
      this.previouslyFocused = document.activeElement

      this.dialog = this.templateTarget.content.firstElementChild.cloneNode(true)
      // Título y jerarquía del triggereo si los hay (texto plano: textContent
      // no inyecta markup; los nombres vienen de datos del usuario). Mismo
      // heading que ModalComponent: título + jerarquía como subtítulo.
      if (this.stashedTitle) {
        this.dialog.querySelector(".tp-modal__title").textContent = this.stashedTitle
        this.dialog.setAttribute("aria-label", this.stashedTitle)
      }
      if (this.stashedHierarchy) {
        const hierarchy = this.dialog.querySelector(".tp-modal__hierarchy")
        hierarchy.textContent = this.stashedHierarchy
        hierarchy.hidden = false
      }
      this.renderMessage(this.dialog.querySelector(".tp-confirm__message"), message)
      ;(document.fullscreenElement || document.body).appendChild(this.dialog)

      // Foco en Cancelar: la respuesta segura por defecto — Enter no ejecuta
      // una acción destructiva por accidente.
      this.dialog.querySelector("[data-v2--confirm-role='deny']").focus()

      // Escape = Cancelar. En captura y deteniendo la propagación: si el
      // confirm se abrió sobre un modal (v2--modal también escucha Escape en
      // document), solo se cierra el confirm, no los dos.
      //
      // Teclado sin mouse: el foco queda ATRAPADO entre los dos botones (Tab
      // y Shift+Tab alternan, nunca salen del diálogo a la página de atrás) y
      // ← / → también alternan. Enter dispara el botón enfocado, como
      // cualquier botón. Sin esto, Tab desde Cancelar se iba al fondo de la
      // página y el confirm quedaba huérfano.
      this._onKeydown = (keyEvent) => {
        if (keyEvent.key === "Escape") {
          keyEvent.preventDefault()
          keyEvent.stopImmediatePropagation()
          this.deny()
          return
        }
        if (["Tab", "ArrowLeft", "ArrowRight"].includes(keyEvent.key)) {
          keyEvent.preventDefault()
          keyEvent.stopImmediatePropagation()
          this.toggleFocus()
        }
      }
      document.addEventListener("keydown", this._onKeydown, true)
    })
  }

  // El mensaje lleva el elemento afectado envuelto en `**...**` (las claves de
  // i18n lo marcan así): va en <strong> para que se distinga del resto. Se
  // construye por nodos de texto y NUNCA por innerHTML — el nombre viene de
  // datos del usuario y no debe poder inyectar markup.
  renderMessage(container, message) {
    message.split("**").forEach((chunk, index) => {
      if (!chunk) return
      if (index % 2 === 1) {
        const strong = document.createElement("strong")
        strong.textContent = chunk
        container.appendChild(strong)
      } else {
        container.appendChild(document.createTextNode(chunk))
      }
    })
  }

  // Alterna el foco entre Cancelar y Confirmar (son dos: alternar basta).
  toggleFocus() {
    const deny = this.dialog.querySelector("[data-v2--confirm-role='deny']")
    const accept = this.dialog.querySelector("[data-v2--confirm-role='accept']")
    ;(document.activeElement === deny ? accept : deny).focus()
  }

  accept() {
    this.close(true)
  }

  deny() {
    this.close(false)
  }

  // Sin backdrop (regla de modales V2): responden Confirmar, Cancelar y
  // Escape (= Cancelar). El confirm no tiene formulario, así que el Escape del
  // autorrelleno no le aplica.
  close(answer) {
    document.removeEventListener("keydown", this._onKeydown, true)
    this._onKeydown = null
    this.dialog.remove()
    this.dialog = null

    this.previouslyFocused?.focus?.()
    this.previouslyFocused = null

    const resolve = this.resolve
    this.resolve = null
    resolve(answer)
  }
}
